import { breakpoints } from 'styles/variables';

interface ChatWidgetConfig {
  header_color?: string;
  send_button_color?: string;
  user_message_color?: string;
  chat_title?: string;
  circle_color?: string;
  seconds_to_autostart?: number;
  widget_location?: string;
  conversation_history?: { author: string; message: string }[];
  allowed_domains?: string[];
}

class ChatWidget {
  private config: ChatWidgetConfig = {};
  private ws: WebSocket | null = null;

  private chatInput: HTMLTextAreaElement | null = null;
  private chatSubmit: HTMLButtonElement | null = null;
  private chatMessages: HTMLElement | null = null;
  private chatBubble: HTMLElement | null = null;
  private chatPopup: HTMLElement | null = null;
  private closePopup: HTMLButtonElement | null = null;
  private chatHistory: HTMLElement | null = null;

  private isFirstMessage: boolean = true;

  constructor(
    private buttonId: string,
    private showNewPoint: boolean,
  ) {}

  public init = (): void => {
    this.insertChat();
    this.loadMessageHistory();
    this.autoSizeWidth();
    this.sendingToChat();
    this.sendingUsingEnter();
    this.textareaCancelNewlineOn();
    this.openChat();
    this.closeChat();
    this.textareaAutoSizeWidth();
    this.resizeChatWidth();
    this.textareaAutoHeight();
    this.checkFirstOpening.init();
    this.chatAutoStart.init();
    this.iconForUnreadMessage.checkNotifLocaleStorage();
    this.insertMetaTag();
  };

  private checkFirstOpening = {
    isOpened: false,
    init: (): void => {
      this.checkFirstOpening.setHandler();
    },
    setHandler: (): void => {
      if (this.chatBubble) {
        this.chatBubble.addEventListener('click', this.checkFirstOpening.handler);
      }
    },
    handler: (): void => {
      this.checkFirstOpening.isOpened = true;
      if (this.chatBubble) {
        this.chatBubble.removeEventListener('click', this.checkFirstOpening.handler);
      }
    },
  };

  private insertChat = (): void => {
    document.head.insertAdjacentHTML(
      'beforeend',
      '<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap" rel="stylesheet">',
    );

    const style = document.createElement('style');
    style.innerHTML = `
  .chat-widget-container * {
    font-family: 'Inter', 'Segoe UI';
    box-sizing: border-box;
    padding: 0px;
    margin: 0px;
    border: none;
    width: auto;
    min-width: auto;
    max-width: auto;
    min-height: auto;
    max-height: auto;
    height: auto;
    background: none;
    box-shadow: none;
    color: initial;
  }
  .chat-widget-container textarea:focus{
    outline: none;
    border: none;
    background-color: #373644;
    box-shadow: none;
  }
  .hidden {
    opacity: 0;
    transform: translate3d(0, 50%, 0);
    z-index: 0;
    pointer-events: none;
  }
  .chat-widget-container {
    position: fixed;
    bottom: 0;
    right: 20px;
    flex-direction: column;
    z-index: 1000;
    transition: transform 3s ease-in-out;
    transform: translate3d(0, 0, 0);
    will-change: transform;
  }
  .chat-bubble{
    font-size: 30px;
    line-height: 36px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: transparent;
    animation-name: opacity-emergence;
    animation-duration: .2s;
    position: relative;
  }
  .chat-bubble svg{
    width: 48px;
    height: 48px;
    color: rgba(255,255,255,1);
    display: block;
    vertical-align: middle;
  }
  .chat-unread-message-icon{
    position: absolute;
    top: -1px;
    right: -1px;
    width: 10px;
    min-width: 10px;
    max-width: 10px;
    height: 10px;
    max-height: 10px;
    min-height: 10px;
    border-radius: 50%;
    background-color: #00D35B;
    animation-name: opacity-emergence;
    animation-duration: .2s;
  }
  .chat-popup{
    height: 40vh;
    max-height: 70vh;
    transition: all .4s ease-in-out;
    will-change: transform, opacity;
    overflow: hidden;
    box-shadow: 0 0 #0000, 0 0 #0000, 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 6px;
    font-size: 14px;
    line-height: 20px;
    background-color: rgba(255,255,255, 1);
    width: 384px;
    max-width: 384px;
    min-width: 384px;
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 20px;
    right: 0;
  }
  .chat-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    background-color: ${this.config?.header_color || 'gray'};
  }
  .chat-header h3{
    font-size: 18px;
    line-height: 28px;
    font-weight: 500;
    margin: 0;
    color: white;
    user-select: none;
  }
  .chat-header #close-popup{
    min-width: 40px !important;
    width: 40px !important;
    height: 40px !important;
    min-height: 40px !important;
    color: rgba(255,255,255,1);
    border-style: none;
    cursor: pointer;
    background-color: transparent;
    background-image: none;
    text-transform: none;
    -webkit-appearance: button;
    font-family: inherit;
    font-size: 100%;
    margin: 0;
    padding: 0px;
    text-indent: 0px;
    text-shadow: none;
    display: flex;
    align-items:center;
    justify-content: center;
    letter-spacing: normal;
    word-spacing: normal;
    text-rendering: auto;
  }
  .chat-header button svg{
    width: 40px;
    height: 40px;
  }
  .chat-messages{
    flex: 1 1 0%;
    padding: 16px;
    overflow-y: auto;
    scrollbar-width: none;
    scrollbar-color: #888 #F2F3F7;
    background-color: #22222A;
  }
  .chat-messages::-webkit-scrollbar {
    width: 3px;
  }
  .chat-messages::-webkit-scrollbar-track {
    background-color: white;
  }
  .chat-messages::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 6px;
  }
  .chat-input-container{
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    border-top: 1px solid var(--border-border-gray-200, #E5E7EB);
    border-top-width: 1px;
    border-color: #373644;
    padding: 16px;
    background: #22222A;
  }
  .chat-input-container-inner{
    display: flex;
    column-gap: 16px;
  }
  .chat-input{
    flex-grow: 1;
    color: white;
    background-color: #373644;
    border-radius: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    padding: 8px 16px;
    outline: none;
    border: none;
    resize: none;
    height: 36px;
    max-height: 136px;
    overflow-y: auto;
    word-break: break-word;
    scrollbar-width: thin;
    scrollbar-color: #888 #F2F3F7;
    user-select: none;
  }
  .chat-input::-webkit-scrollbar {
    width: 3px;
  }
  .chat-input::-webkit-scrollbar-track {
    background-color: white;
  }
  .chat-input::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 6px;
  }
  .chat-submit{
    min-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
    min-height: 36px !important;
    padding: 6px;
    border-radius: 50%;
    background-color: ${this.config?.send_button_color || 'gray'};
    border: none;
    align-self: flex-end;
    cursor: pointer;
  }
  .chat-submit svg{
    width: 24px;
    height: 24px;
    fill: white;
  }
  .chat-message-user{
    max-width: 80%;
    color: rgba(255,255,255,1);
    padding: 8px 16px;
    background-color: ${this.config?.user_message_color || 'gray'};
    word-break: break-word;
    border-radius: 8px 8px 2px 8px;
  }
  .chat-message-user-container{
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
  .chat-message-bot-container{
    display: flex;
    margin-bottom: 12px;
  }
  .chat-message-bot{
    max-width: 80%;
    color: white;
    padding: 8px 16px;
    background: #373644;
    word-break: break-word;
    border-radius: 8px 8px 8px 2px;
  }
  .chat-message-bot a {
    color: lightblue;
    text-decoration: underline;
    cursor: pointer;
  }
  @media (max-width: 768px) {
    .chat-widget-container {
      height: calc(100dvh - env(safe-area-inset-bottom));
    }

    .chat-widget-container {
      right: 0;
    }

    .chat-popup {
      position: fixed;
      top: 0;
      right: 0;
      width: 100vw;
      height: calc(100dvh - env(safe-area-inset-bottom));
      max-height: 100%;
      border-radius: 0;
      max-width: initial;
      min-width: initial;
    }
    .chat-header{
      border-radius: 0px;
    }
  }
  .chat-online{
    font-size: 14px;
    line-height: 20px;
    color: #fff;
  }
  @keyframes opacity-emergence{
    0%{
      opacity: 0;
    }
    100%{
      opacity: 1
    }
  }
  .chat-prints ~ .chat-online{
    display: none;
  }
  .chat-prints{
    display: flex;
    align-items: center;
    color: #fff;
  }
  .chat-prints-circle-container{
      display: flex;
      align-items: center;
      column-gap: 4px;
      margin-right: 5px;
  }

  .chat-prints-circle{
      min-width: 3px;
      width: 3px;
      max-width: 3px;
      height: 3px;
      max-height: 3px;
      min-height: 3px;
      border-radius: 50%;
      background-color: #fff;
  }

  .chat-prints-circle-first{
      animation-name: chat-prints-circle-first;
      animation-duration: 1.5s;
      animation-iteration-count: infinite;
  }
  .chat-prints-circle-second{
      animation-name: chat-prints-circle-second;
      animation-duration: 1.5s;
      animation-iteration-count: infinite;
  }
  .chat-prints-circle-third{
      animation-name: chat-prints-circle-third;
      animation-duration: 1.5s;
      animation-iteration-count: infinite;
  }
  .chat-lath{
    display: flex;
    align-items: center;
    align-self: center;
    column-gap: 4px;
    text-decoration: none;
  }

  .chat-lath-text{
    font-size: 12px;
    font-weight: 500;
    color: #A9ABBA;
    line-height: 16px;
  }

  @keyframes chat-prints-circle-first{
      0%{
          transform: scale(1);
      }
      16.5%{
          transform: scale(1.5);
      }
      33%{
          transform: scale(1);
      }
      100%{
          transform: scale(1);
      }

  }
  @keyframes chat-prints-circle-second{
      0%{
          transform: scale(1);
      }
      33%{
          transform: scale(1);
      }
      49.5%{
          transform: scale(1.5);
      }
      66%{
          transform: scale(1);
      }
      100%{
          transform: scale(1);
      }
  }
  @keyframes chat-prints-circle-third{
      0%{
          transform: scale(1);
      }
      66%{
          transform: scale(1);
      }
      80.5%{
          transform: scale(1.5);
      }
      99%{
          transform: scale(1);
      }
      100%{
          transform: scale(1);
      }
    }
    #close-popup svg path {
      stroke: #FFF;
    }
  `;

    document.head.appendChild(style);

    const container = document.createElement('div');
    container.id = 'chat-widget-container';
    container.classList.add('chat-widget-container');
    document.body.appendChild(container);

    // safari need to use inline styles for default hide chat
    container.innerHTML = `
    <div id="chat-popup" class="chat-popup" style="opacity: 0; transform: translate3d(0, 50%, 0); z-index: 0; pointer-events: none;">
    <div id="chat-header" class="chat-header">
        <div>
          <h3>${this.config.chat_title || ''}</h3>
          <div class="chat-status">
            <div class="chat-online">Онлайн</div>
          </div>

        </div>
        <button id="close-popup">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.25 11L12.2879 15.7378C11.9218 16.0874 11.3282 16.0874 10.9621 15.7378L6 11"
            stroke="white"
            strokeLinecap="round"
          />
        </svg>
        </button>
      </div>
      <div id="maia-chat-messages" class="chat-messages">
        <div id="chat-history" class="chat-history"></div>
      </div>
      <div id="chat-input-container" class="chat-input-container">
        <div class="chat-input-container-inner">
          <textarea id="chat-input" class="chat-input" placeholder="Написать сообщение..."></textarea>
          <button id="chat-submit" class="chat-submit">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 5V19M12 5L18 11M12 5L6 11" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;

    this.elementSearch();
  };

  private elementSearch = (): void => {
    this.chatInput = document.getElementById('chat-input') as HTMLTextAreaElement;
    this.chatSubmit = document.getElementById('chat-submit') as HTMLButtonElement;
    this.chatMessages = document.getElementById('maia-chat-messages');
    this.chatBubble = document.getElementById(this.buttonId);
    this.chatPopup = document.getElementById('chat-popup');
    this.closePopup = document.getElementById('close-popup') as HTMLButtonElement;
    this.chatHistory = document.getElementById('chat-history');
  };

  private autoSizeWidth = (): void => {
    if (window.innerWidth >= breakpoints.TabletWidth) {
      if (this.chatMessages && this.chatMessages.clientHeight < this.chatMessages.scrollHeight) {
        this.chatMessages.style.paddingRight = '13px';
      }
    }
  };

  private messagesContainerCheckWidth = (): void => {
    if (this.chatMessages) {
      if (
        window.innerWidth > breakpoints.TabletWidth &&
        this.chatMessages.clientHeight < this.chatMessages.scrollHeight
      ) {
        this.chatMessages.style.paddingRight = '13px';
      } else {
        this.chatMessages.style.paddingRight = '16px';
      }
    }
  };

  private resizeChatWidth = (): void => {
    window.addEventListener('resize', () => {
      this.messagesContainerCheckWidth();
    });
  };

  private textareaCheckWidth = (): void => {
    if (this.chatInput) {
      if (window.innerWidth < breakpoints.TabletWidth) return;
      if (parseFloat(getComputedStyle(this.chatInput).maxHeight) < this.chatInput.scrollHeight) {
        this.chatInput.style.paddingRight = '13px';
      } else {
        this.chatInput.style.paddingRight = '16px';
      }
    }
  };

  private textareaAutoSizeWidth = (): void => {
    this.chatInput?.addEventListener('input', () => {
      this.textareaCheckWidth();
    });
  };

  private textareaCheckHeight = (): void => {
    if (!this.chatInput) return;
    this.chatInput.style.height = '36px';
    this.chatInput.style.height = this.chatInput.scrollHeight + 'px';
  };

  private textareaAutoHeight = (): void => {
    this.chatInput?.addEventListener('input', () => {
      this.textareaCheckHeight();
    });
  };

  private sendingToChat = (): void => {
    this.chatSubmit?.addEventListener('click', async () => {
      if (!this.chatInput) return;
      const value = this.chatInput.value.trim();
      if (value) {
        if (this.chatMessages) {
          this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
        }
        this.onUserRequest(value);
        this.prints.printsAdd();
        this.autoSizeWidth();
        this.textareaCheckHeight();
      }
    });
  };

  private sendingUsingEnter = (): void => {
    this.chatInput?.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        this.chatSubmit?.click();
        this.autoSizeWidth();
      }
    });
  };

  private textareaCancelNewlineOn = (): void => {
    this.chatInput?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });
  };

  private openChat = (): void => {
    let prevInnerWidth = window.innerWidth;

    const clickCb = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.togglePopup();
    };

    window.addEventListener('resize', () => {
      if (
        (prevInnerWidth <= breakpoints.TabletWidth &&
          window.innerWidth > breakpoints.TabletWidth) ||
        (prevInnerWidth > breakpoints.TabletWidth && window.innerWidth <= breakpoints.TabletWidth)
      ) {
        if (this.chatBubble) this.chatBubble.removeEventListener('click', clickCb);
        this.chatBubble = document.getElementById('chat-bubble');
        this.chatBubble?.addEventListener('click', clickCb);
      }
      prevInnerWidth = window.innerWidth;
    });

    this.chatBubble?.addEventListener('click', clickCb);
  };

  private closeChat = (): void => {
    this.closePopup?.addEventListener('click', () => {
      this.togglePopup();
    });
  };

  private togglePopup = (): void => {
    if (this.chatPopup) this.chatPopup.style.cssText = '';

    this.chatPopup?.classList.toggle('hidden');

    if (window.innerWidth <= breakpoints.TabletWidth) {
      document.querySelector('body').style.overflow = this.chatPopup.classList.value.includes(
        'hidden',
      )
        ? ''
        : 'hidden';
    }
  };

  private scrollWindowToBottom = (): void => {
    if (!this.chatPopup || !this.chatMessages) return;
    this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
    this.chatPopup.classList.add('hidden');
  };

  private chatAutoStart = {
    init: (): void => {
      this.chatAutoStart.setTimeoutAutoStart();
    },
    setTimeoutAutoStart: (): void => {
      if (this.config?.seconds_to_autostart) {
        setTimeout(
          () => {
            if (
              !this.checkFirstOpening.isOpened &&
              this.chatPopup?.classList.contains('hidden') &&
              this.chatBubble
            ) {
              this.chatBubble.click();
            }
          },
          1000 * (this.config.seconds_to_autostart || 0),
        );
      }
    },
  };

  private userMessageAppend = (message: string): void => {
    const msg = this.getUserMessage(message);
    this.chatMessages?.appendChild(msg);
    if (this.chatMessages) {
      this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
    }
    if (this.chatInput) this.chatInput.value = '';
  };

  private onUserRequest = (message: string): void => {
    try {
      this.userMessageAppend(message);
      if (this.ws) {
        this.ws.send(
          JSON.stringify({
            action: 'MESSAGE',
            session_id: localStorage.getItem('session_id'),
            message: message,
          }),
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

  private getBotMessage = (message: string): HTMLElement => {
    const text = this.convertingFunction.parseLinks(message);
    const container = document.createElement('div');
    container.className = 'flex mb-3 chat-message-bot-container';
    container.innerHTML = `<div class="chat-message-bot">${text}</div>`;
    return container;
  };

  private getUserMessage = (message: string): HTMLElement => {
    const container = document.createElement('div');
    container.className = 'chat-message-user-container';
    container.innerHTML = `<div class="chat-message-user">${message}</div>`;
    return container;
  };

  private loadMessageHistory = (): void => {
    if (!this.config.conversation_history) return;
    this.config.conversation_history.forEach(({ author, message }) => {
      const msgElement =
        author === 'USER' ? this.getUserMessage(message) : this.getBotMessage(message);
      this.chatHistory?.append(msgElement);
    });
    this.scrollWindowToBottom();
  };

  public reply = (message: string): void => {
    this.messageSound.audioPlay();
    const botMsg = this.getBotMessage(message);
    this.chatMessages?.appendChild(botMsg);
    if (this.chatMessages) {
      this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
    }
    this.autoSizeWidth();
  };

  public openWebSocket = (
    url: string = 'apidev.maia.work',
    appId: string,
    apiHash: string,
    profileId: number | null,
  ): void => {
    this.ws = new WebSocket(
      `wss://${url}/api/v1/webhook/chat_widget?app_id=${appId}&api_hash=${apiHash}`,
    );
    this.webSocketOpenHandler();
    this.webSocketMessageHandler(profileId);
    this.webSocketCloseHandler();
    this.websocketClosure();
  };

  private webSocketOpenHandler = (): void => {
    if (!this.ws) return;
    this.ws.onopen = () => {
      this.ws?.send(
        JSON.stringify({
          action: 'INIT',
          session_id: localStorage.getItem('session_id') || undefined,
        }),
      );
    };
  };

  private webSocketMessageHandler = (profileId: number | null): void => {
    if (!this.ws) return;
    this.ws.onmessage = (event) => {
      const data = JSON.parse(JSON.parse(event.data));
      if (data.action === 'INIT') {
        if (
          !localStorage.getItem('session_id') ||
          localStorage.getItem('session_id') !== `${profileId}`
        ) {
          localStorage.setItem('session_id', `${profileId}`);
        } else {
          localStorage.setItem('session_id', data.session_id);
        }
        this.writingDataToConfig(data);
        this.init();
        this.messageSound.init();
        this.iconForUnreadMessage.init();
      } else if (data.action === 'MESSAGE') {
        this.reply(data.answer);
        this.iconForUnreadMessage.messageCheck();
        this.prints.printsRemove();
      }
    };
  };

  private webSocketCloseHandler = (): void => {
    if (!this.ws) return;
    this.ws.onclose = () => {
      localStorage.removeItem('session_id');
      this.chatInput?.setAttribute('disabled', 'true');
    };
  };

  private websocketClosure = (): void => {
    document.addEventListener('unload', () => {
      localStorage.removeItem('session_id');
      this.ws?.close();
    });
  };

  private writingDataToConfig = (data: any): void => {
    this.config = {
      ...this.config,
      // @ts-expect-error o1 issue
      salesperson_name: data.salesperson_name,
      salesperson_role: data.salesperson_role,
      chat_title: data.chat_title,
      circle_color: data.circle_color,
      header_color: data.header_color,
      seconds_to_autostart: data.seconds_to_autostart,
      send_button_color: data.send_button_color,
      user_message_color: data.user_message_color,
      widget_location: data.widget_location,
      conversation_history: data.conversation_history,
      allowed_domains: data.allowed_domains,
    };
  };

  private prints = {
    callArray: [] as boolean[],
    printsAdd: (): void => {
      const statusEl = document.querySelector('.chat-status');
      if (statusEl && !statusEl.querySelector('[data-chat-prints-loading]')) {
        statusEl.prepend(this.prints.getPrintsHTML());
      }
    },
    printsRemove: (): void => {
      document.querySelector('.chat-status [data-chat-prints-loading]')?.remove();
    },
    getPrintsHTML: (): HTMLElement => {
      const div = document.createElement('div');
      div.innerHTML = `<div class="chat-prints" data-chat-prints-loading>
                                    <div class="chat-prints-circle-container">
                                      <div class="chat-prints-circle chat-prints-circle-first"></div>
                                      <div class="chat-prints-circle chat-prints-circle-second"></div>
                                      <div class="chat-prints-circle chat-prints-circle-third"></div>
                                    </div>
                                    печатает
                                  </div>`;
      return div.firstElementChild as HTMLElement;
    },
  };

  private messageSound = {
    sound: null as HTMLAudioElement | null,
    init: (): void => {
      this.messageSound.createAudio();
    },
    createAudio: (): void => {
      this.messageSound.sound = document.createElement('audio');
      this.messageSound.sound.src =
        'https://b6905acf-5120-4e0c-b3ea-3716bba15c68.selstorage.ru/message_sound.mp3';
      this.messageSound.sound.addEventListener('error', () => {
        this.messageSound.sound = null;
      });
    },
    audioPlay: (): void => {
      if (this.messageSound.sound && !this.isFirstMessage) {
        if (!this.messageSound.sound.paused) {
          this.messageSound.sound.pause();
          this.messageSound.sound.currentTime = 0;
        }
        this.messageSound.sound.play();
      }

      this.isFirstMessage = false;
    },
  };

  private iconForUnreadMessage = {
    init: (): void => {
      this.iconForUnreadMessage.chatOpenHandler();
    },
    messageCheck: (): void => {
      if (this.iconForUnreadMessage.closeDiheck()) {
        this.iconForUnreadMessage.showIcon();
        this.iconForUnreadMessage.setNotifLocaleStorage();
      }
    },
    closeDiheck: (): boolean => {
      const t = document.querySelector('#chat-popup');
      if (t) return t.classList.contains('hidden');
      return false;
    },
    showIcon: (): void => {
      if (this.showNewPoint) return;
      let t = document.querySelector('#chat-unread-message-icon');
      if (!t) {
        // create icon if not exists
        const icon = document.createElement('div');
        icon.id = 'chat-unread-message-icon';
        icon.className = 'chat-unread-message-icon';
        this.chatBubble?.appendChild(icon);
        t = icon;
      }
      t.classList.remove('hidden');
    },
    setNotifLocaleStorage: (): void => {
      localStorage.setItem('unred-message', 'true');
    },
    removeNotifLocaleStorage: (): void => {
      localStorage.removeItem('unred-message');
    },
    checkNotifLocaleStorage: (): void => {
      if (localStorage.getItem('unred-message')) {
        this.iconForUnreadMessage.showIcon();
      }
    },
    hiddenIcon: (): void => {
      const t = document.querySelector('#chat-unread-message-icon');
      t?.classList.add('hidden');
    },
    chatOpenHandler: (): void => {
      const t = document.querySelector('#chat-bubble');
      if (t) {
        t.addEventListener('click', () => {
          if (!this.iconForUnreadMessage.closeDiheck()) {
            this.iconForUnreadMessage.hiddenIcon();
            this.iconForUnreadMessage.removeNotifLocaleStorage();
          }
        });
      }
    },
  };

  private insertMetaTag = (): void => {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      // @ts-expect-error o1 issue
      meta.name = 'viewport';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
    // @ts-expect-error o1 issue
    if (meta && meta.content) {
      // @ts-expect-error o1 issue
      if (meta.content.includes(', user-scalable=no')) {
        // @ts-expect-error o1 issue
        meta.content = 'width=device-width, initial-scale=1.0, user-scalable=no';
      } else {
        // @ts-expect-error o1 issue
        meta.content += ', user-scalable=no';
      }
    } else if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=no');
    }
  };

  private convertingFunction = {
    parseLinks: (text: string): string => {
      const urlRegex =
        /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
      return text.replace(urlRegex, function (url) {
        return '<a href="' + url + '" target="_blank">' + url + '</a>';
      });
    },
  };
}

const isMaiaInit: { [key: string]: boolean } = {};

const init = (
  buttonId: string,
  showNewPoint: boolean,
  id: string,
  hash: string,
  profileId: number | null,
) => {
  console.log('init maia chat');
  if (isMaiaInit[buttonId]) return;

  new ChatWidget(buttonId, showNewPoint).openWebSocket('api.maia.work', id, hash, profileId);
  isMaiaInit[buttonId] = true;
};

export default init;
